import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { KeyRound, Eye, EyeOff, CheckCircle } from 'lucide-react'
import { supabase } from '../lib/supabase'

// Ziel des Recovery-Links aus der Passwort-vergessen-Mail.
//
// Bewusst NICHT in <ProtectedRoute> eingehängt: dort werden Konten mit
// status 'pending' oder 'rejected' mit einer Vollbild-Sperre abgefangen,
// die könnten ihr Passwort sonst nie zurücksetzen.
//
// Kein Token-Parsing nötig — src/lib/supabase.ts läuft mit
// flowType 'pkce' und detectSessionInUrl: true, supabase-js tauscht den
// ?code= aus der URL beim Laden selbst gegen eine Session. Das passiert
// asynchron, deshalb unten zusätzlich onAuthStateChange abwarten statt
// nur einmal getSession() zu prüfen.

const GREEN = '#1B7E3A'
const MIN_LENGTH = 8

const card = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 14,
  boxShadow: '0 1px 3px rgba(0,0,0,.06)',
}

const inputStyle = {
  padding: '10px 12px',
  border: '1px solid #d1d5db',
  borderRadius: 10,
  fontSize: 15,
  fontFamily: 'inherit',
  width: '100%',
  boxSizing: 'border-box' as const,
}

export default function ResetPasswordPage() {
  const navigate = useNavigate()

  const [checking, setChecking] = useState(true)
  const [hasSession, setHasSession] = useState(false)
  // Wird bewusst erst beim Absenden eingelöst, nicht beim Laden der Seite.
  const [tokenHash, setTokenHash] = useState<string | null>(null)
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [show, setShow] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  useEffect(() => {
    let cancelled = false

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (cancelled || !session) return
      setHasSession(true)
      setChecking(false)
    })

    void (async () => {
      // ---------------------------------------------------------------------
      // Bevorzugter Weg: token_hash aus der Query — und hier BEWUSST nicht
      // eingelöst.
      //
      // Der klassische Link zeigt auf /auth/v1/verify, und schon ein GET
      // darauf verbraucht den Einmal-Token. Genau das ist passiert: im
      // Auth-Log rief 185.194.178.x (ein Rechenzentrum, nicht der Anschluss
      // des Nutzers) den Link ab, danach lieferte jeder echte Klick nur noch
      // "One-time token not found". Mail-Scanner und Link-Vorschauen tun das
      // routinemässig.
      //
      // Mit token_hash passiert beim Laden nichts — der Token wird erst in
      // submit() per verifyOtp() eingelöst, also durch eine bewusste
      // Nutzeraktion. Ein Scanner, der die URL abruft, lädt nur die SPA.
      // ---------------------------------------------------------------------
      const query = new URLSearchParams(window.location.search)
      const qToken = query.get('token_hash')
      if (qToken && query.get('type') === 'recovery') {
        if (cancelled) return
        setTokenHash(qToken)
        setChecking(false)
        return
      }

      // Älterer Weg, für bereits verschickte Mails: GoTrue leitet im
      // Implicit-Stil zurück, mit den Tokens im Hash (#access_token=…),
      // NICHT als ?code=. Unser Client läuft auf flowType 'pkce' und sucht
      // nach ?code=, greift den Hash also nicht auf — im Browser verifiziert,
      // die Konsole meldete nur "INITIAL_SESSION undefined".
      const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''))

      // Abgelaufene oder verbrauchte Links kommen als Fehler im Hash zurück.
      const hashError = hash.get('error_description') || hash.get('error')
      const accessToken = hash.get('access_token')
      const refreshToken = hash.get('refresh_token')

      if (accessToken && refreshToken) {
        const { data, error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        })
        // Tokens aus URL und Verlauf entfernen — sie sind vollwertige
        // Zugangsdaten und haben in der Adressleiste nichts verloren.
        window.history.replaceState({}, '', window.location.pathname)
        if (cancelled) return
        if (data?.session && !error) {
          setHasSession(true)
          setChecking(false)
          return
        }
        setErr(error?.message ?? null)
        setChecking(false)
        return
      }

      if (hashError) {
        window.history.replaceState({}, '', window.location.pathname)
        if (cancelled) return
        setChecking(false)
        return
      }

      // PKCE-Variante (?code=) und der Fall "bereits angemeldet".
      const { data } = await supabase.auth.getSession()
      if (cancelled) return
      if (data?.session) {
        setHasSession(true)
        setChecking(false)
        return
      }
      setTimeout(() => {
        if (!cancelled) setChecking(false)
      }, 2500)
    })()

    return () => {
      cancelled = true
      sub.subscription.unsubscribe()
    }
  }, [])

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setErr(null)

    if (password.length < MIN_LENGTH) {
      setErr(`Das Passwort muss mindestens ${MIN_LENGTH} Zeichen lang sein.`)
      return
    }
    if (password !== confirm) {
      setErr('Die beiden Passwörter stimmen nicht überein.')
      return
    }

    setBusy(true)

    // Der Token wird genau hier eingelöst, ausgelöst durch das Absenden des
    // Formulars — nicht schon beim Öffnen der Seite. Ein Scanner, der die URL
    // bloss abruft, verbraucht ihn damit nicht mehr.
    if (tokenHash) {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: 'recovery',
      })
      if (verifyError) {
        setBusy(false)
        setErr(
          verifyError.message.includes('expired') || verifyError.message.includes('not found')
            ? 'Dieser Link ist nicht mehr gültig. Bitte fordere einen neuen an.'
            : verifyError.message
        )
        return
      }
      // Der Token ist jetzt eingelöst und die Session steht. Ohne dieses
      // Zurücksetzen würde ein zweiter Absende-Versuch (etwa nach einem
      // updateUser-Fehler wie "gleiches Passwort") denselben, bereits
      // verbrauchten Token erneut einlösen wollen — und fälschlich
      // "Link nicht mehr gültig" melden, obwohl nur das Passwort zu
      // korrigieren wäre.
      setTokenHash(null)
      setHasSession(true)
    }

    // Nie unbegrenzt hängen bleiben: sonst steht der Nutzer ohne Rückmeldung
    // auf "Speichere…" und weiss nicht, ob das Passwort gesetzt wurde.
    const timeout = new Promise<{ error: { message: string } }>((resolve) =>
      setTimeout(
        () => resolve({ error: { message: 'Zeitüberschreitung. Bitte Seite neu laden und erneut versuchen.' } }),
        15000
      )
    )
    const { error } = await Promise.race([supabase.auth.updateUser({ password }), timeout])
    setBusy(false)

    if (error) {
      setErr(
        error.message.includes('different from the old')
          ? 'Das neue Passwort muss sich vom alten unterscheiden.'
          : error.message
      )
      return
    }

    setDone(true)

    // Abmelden, damit die Recovery-Session nicht bestehen bleibt und bewusst
    // mit dem neuen Passwort angemeldet wird.
    //
    // BEWUSST NICHT awaited: der Passwortwechsel entwertet die Refresh-Tokens,
    // danach läuft signOut() gegen einen gerade ungültig gewordenen Token und
    // hängt in Retries und dem internen Lock von supabase-js. Vorher blockierte
    // das die Erfolgsmeldung — das Passwort war längst gesetzt, die Seite stand
    // aber weiter auf "Speichere…".
    void supabase.auth.signOut().catch(() => {})
    setTimeout(() => navigate('/login', { replace: true }), 1500)
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ ...card, width: '100%', maxWidth: 440, padding: 28 }}>

        <h1 style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 22, fontWeight: 800, color: '#111', margin: '0 0 6px' }}>
          <KeyRound size={24} color={GREEN} /> Neues Passwort
        </h1>

        {checking ? (
          <p style={{ color: '#6b7280', margin: '18px 0 0' }}>Link wird geprüft…</p>
        ) : done ? (
          <div style={{ marginTop: 18 }}>
            <p style={{ display: 'flex', alignItems: 'center', gap: 8, color: GREEN, fontWeight: 600, margin: 0 }}>
              <CheckCircle size={18} /> Passwort geändert.
            </p>
            <p style={{ color: '#6b7280', margin: '10px 0 0', fontSize: 14 }}>
              Sie werden zur Anmeldung weitergeleitet…
            </p>
          </div>
        ) : !hasSession && !tokenHash ? (
          <div style={{ marginTop: 18 }}>
            <p style={{ color: '#374151', margin: '0 0 16px', lineHeight: 1.6 }}>
              Dieser Link ist ungültig oder bereits abgelaufen. Recovery-Links lassen
              sich nur einmal verwenden.
            </p>
            <button
              type="button"
              onClick={() => navigate('/login')}
              style={{ padding: '11px 18px', background: GREEN, color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}
            >
              Zurück zur Anmeldung
            </button>
          </div>
        ) : (
          <form onSubmit={submit} style={{ marginTop: 18 }}>
            <p style={{ color: '#6b7280', margin: '0 0 20px', fontSize: 14, lineHeight: 1.6 }}>
              Wählen Sie ein neues Passwort für Ihren FC&nbsp;Frick Zugang.
            </p>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 14 }}>
              Neues Passwort
              <div style={{ position: 'relative' }}>
                <input
                  type={show ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                  minLength={MIN_LENGTH}
                  style={{ ...inputStyle, paddingRight: 42 }}
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  aria-label={show ? 'Passwort verbergen' : 'Passwort anzeigen'}
                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 4, lineHeight: 0 }}
                >
                  {show ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 18 }}>
              Passwort bestätigen
              <input
                type={show ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                required
                style={inputStyle}
              />
            </label>

            <button
              type="submit"
              disabled={busy}
              style={{ width: '100%', padding: '12px 18px', background: GREEN, color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 }}
            >
              {busy ? 'Speichere…' : 'Passwort speichern'}
            </button>

            {err && <p style={{ color: '#b91c1c', fontWeight: 600, margin: '14px 0 0', fontSize: 14 }}>{err}</p>}
          </form>
        )}
      </div>
    </div>
  )
}
